<template>
  <transition name="menu-slide">
    <div
      v-if="show"
      v-on-clickaway="onClickAway"
      class="left-3 rtl:left-auto rtl:right-3 bottom-16 w-64 absolute z-99 rounded-md shadow-xl bg-white dark:bg-slate-800 py-2 px-2 border border-slate-25 dark:border-slate-700"
      :class="{ 'block visible': show }"
    >
      <availability-status />
      <woot-dropdown-menu>
        <woot-dropdown-item v-if="showChangeAccountOption">
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="arrow-swap"
            @click="$emit('toggle-accounts')"
          >
            {{ $t('SIDEBAR_ITEMS.CHANGE_ACCOUNTS') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item v-if="globalConfig.chatwootInboxToken">
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="chat-help"
            @click="$emit('show-support-chat-window')"
          >
            {{ $t('SIDEBAR_ITEMS.CONTACT_SUPPORT') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item>
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="keyboard"
            @click="handleKeyboardHelpClick"
          >
            {{ $t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item>
          <router-link
            v-slot="{ href, isActive, navigate }"
            :to="`/app/accounts/${accountId}/profile/settings`"
            custom
          >
            <a
              :href="href"
              class="button small clear secondary bg-white dark:bg-slate-800 h-8"
              :class="{ 'is-active': isActive }"
              @click="e => handleProfileSettingClick(e, navigate)"
            >
              <fluent-icon icon="person" size="14" class="icon icon--font" />
              <span class="button__content">
                {{ $t('SIDEBAR_ITEMS.PROFILE_SETTINGS') }}
              </span>
            </a>
          </router-link>
        </woot-dropdown-item>
        <woot-dropdown-item>
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="appearance"
            @click="openAppearanceOptions"
          >
            {{ $t('SIDEBAR_ITEMS.APPEARANCE') }}
          </woot-button>
        </woot-dropdown-item>
        <woot-dropdown-item v-if="currentUser.type === 'SuperAdmin'">
          <a
            href="/super_admin"
            class="button small clear secondary bg-white dark:bg-slate-800 h-8"
            target="_blank"
            rel="noopener nofollow noreferrer"
            @click="$emit('close')"
          >
            <fluent-icon
              icon="content-settings"
              size="14"
              class="icon icon--font"
            />
            <span class="button__content">
              {{ $t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE') }}
            </span>
          </a>
        </woot-dropdown-item>
        <woot-dropdown-item>
          <woot-button
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="power"
            @click="logout"
          >
            {{ $t('SIDEBAR_ITEMS.LOGOUT') }}
          </woot-button>
        </woot-dropdown-item>
      </woot-dropdown-menu>
    </div>
  </transition>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';
import Auth from '../../../api/auth';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu';
import AvailabilityStatus from 'dashboard/components/layout/AvailabilityStatus';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

export default {
  components: {
    WootDropdownMenu,
    WootDropdownItem,
    AvailabilityStatus,
  },
  mixins: [clickaway],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
    }),
    showChangeAccountOption() {
      if (this.globalConfig.createNewAccountFromDashboard) {
        return true;
      }

      const { accounts = [] } = this.currentUser;
      return accounts.length > 1;
    },
  },
  methods: {
    handleProfileSettingClick(e, navigate) {
      this.$emit('close');
      navigate(e);
    },
    handleKeyboardHelpClick() {
      this.$emit('key-shortcut-modal');
      this.$emit('close');
    },
    logout() {
      Auth.logout();
    },
    onClickAway() {
      if (this.show) this.$emit('close');
    },
    getCurrentThemeKey() {
      const colorScheme = LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME);
      return ['light', 'dark', 'auto'].includes(colorScheme)
        ? colorScheme
        : 'auto';
    },
    focusCurrentThemeOption(ninja, themeKey) {
      const actionItems = Array.from(
        ninja?.shadowRoot?.querySelectorAll('ninja-action') || []
      );
      const selectedAction = actionItems.find(
        item => item?.action?.id === themeKey
      );

      if (selectedAction) {
        selectedAction.dispatchEvent(
          new MouseEvent('mouseover', { bubbles: true, composed: true })
        );
      }
    },
    syncCurrentThemeTick(ninja, themeKey) {
      const themeKeys = ['light', 'dark', 'auto'];
      const actionItems = Array.from(
        ninja?.shadowRoot?.querySelectorAll('ninja-action') || []
      );

      actionItems.forEach(item => {
        const actionKey = item?.action?.id;
        const actionRow = item?.shadowRoot?.querySelector('.ninja-action');
        if (!themeKeys.includes(actionKey) || !actionRow) {
          return;
        }

        const existingTick = actionRow.querySelector('.appearance-theme-tick');
        if (existingTick) {
          existingTick.remove();
        }

        actionRow.style.paddingRight = '1rem';
        if (actionKey !== themeKey) {
          return;
        }

        const tick = document.createElement('span');
        tick.className = 'appearance-theme-tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.textContent = '\u2713';
        Object.assign(tick.style, {
          marginLeft: 'auto',
          color: '#22c55e',
          fontSize: '16px',
          fontWeight: '700',
          lineHeight: '1',
        });
        actionRow.appendChild(tick);
      });
    },
    async openAppearanceOptions() {
      const ninja = document.querySelector('ninja-keys');
      if (!ninja) return;

      ninja.open({ parent: 'appearance_settings' });
      const currentThemeKey = this.getCurrentThemeKey();
      if (ninja.updateComplete) {
        await ninja.updateComplete;
      }
      await this.$nextTick();
      requestAnimationFrame(() => {
        this.syncCurrentThemeTick(ninja, currentThemeKey);
        this.focusCurrentThemeOption(ninja, currentThemeKey);
      });
    },
  },
};
</script>
